import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, CompanyResponse, saveAuthToken, clearAuthData } from '@/shared/api';
import { useToast } from '@/shared/ui';

// Define the structure of our user type
export interface User {
  email: string;
  id?: number;
  firstName?: string;
  lastName?: string;
  username?: string;
  [key: string]: any; // Allow for additional properties
}

// Define the shape of our context
interface AuthContextType {
  user: User | null;
  company: CompanyResponse | null;
  isLoggedIn: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  fetchCompany: () => Promise<CompanyResponse>;
  refreshCompanyData: () => Promise<CompanyResponse>;
  checkAndRedirectCompanySetup: () => Promise<void>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<CompanyResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [companyFetchFailed, setCompanyFetchFailed] = useState<boolean>(false);
  const fetchingCompanyRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();

  // Fetch company information - returns existing data if available or fetches from API
  const fetchCompany = async (): Promise<CompanyResponse> => {
    console.log('🏢 fetchCompany called, current state:', { 
      isLoggedIn, 
      companyFetchFailed, 
      hasCompany: !!company,
      fetchingInProgress: fetchingCompanyRef.current,
      companyData: company ? { ...company } : null
    });

    // Don't attempt to fetch if we're not logged in or we've already tried and failed
    if (!isLoggedIn || companyFetchFailed) {
      throw new Error(isLoggedIn ? 'Company fetch previously failed' : 'Not logged in');
    }
    
    // If we already have company data, just return it without making another API call
    if (company) {
      console.log('🏢 Returning cached company data:', company);
      return company;
    }

    // Prevent duplicate calls
    if (fetchingCompanyRef.current) {
      console.log('🏢 Company fetch already in progress, waiting...');
      // Wait for the current fetch to complete
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!fetchingCompanyRef.current) {
            clearInterval(checkInterval);
            if (company) {
              resolve(company);
            } else {
              reject(new Error('Company fetch failed'));
            }
          }
        }, 100);
      });
    }
    
    try {
      console.log('🏢 Fetching company data from API...');
      fetchingCompanyRef.current = true;
      const companyData = await authApi.getCompany();
      console.log('🏢 API response received:', companyData);
      setCompany(companyData);
      
      // Let the caller handle navigation logic - this is just for getting data
      
      return companyData;
    } catch (error: any) {
      console.error('Failed to fetch company data:', error);
      
      // Show error toast and mark as failed to prevent retries
      showError(error.message || 'Failed to fetch company information');
      setCompanyFetchFailed(true);
      
      throw error;
    } finally {
      fetchingCompanyRef.current = false;
    }
  };

  // Check if the user is already logged in when the component mounts
  useEffect(() => {
    console.log('🔄 AuthContext useEffect triggered - checking logged in status');
    const checkLoggedIn = () => {
      const lsUser = localStorage.getItem('user');
      const ssUser = sessionStorage.getItem('user');
      const lsToken = localStorage.getItem('token');
      const ssToken = sessionStorage.getItem('token');
      const lsLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
      const ssLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
      
      console.log('🔄 AuthContext checkLoggedIn:', {
        lsUser: !!lsUser,
        ssUser: !!ssUser,
        lsToken: !!lsToken,
        ssToken: !!ssToken,
        lsLoggedIn,
        ssLoggedIn
      });
      
      if ((lsLoggedIn && lsUser && lsToken) || (ssLoggedIn && ssUser && ssToken)) {
        const userData = lsUser ? JSON.parse(lsUser) : JSON.parse(ssUser || '{}');
        console.log('🔄 AuthContext: Setting user from storage:', userData);
        setUser(userData);
        setIsLoggedIn(true);
        
        // Don't fetch company data automatically on initial load
        // Company data will only be fetched after explicit login action
        console.log('AuthContext: User restored from storage, but not fetching company data');
      } else {
        console.log('🔄 AuthContext: No valid session found');
      }
      setIsLoading(false);
    };
    
    checkLoggedIn();
  }, []);

  // Login function
  const login = async (email: string, password: string, rememberMe: boolean): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Make the API call to authenticate the user
      const response = await authApi.login(email, password);
      
      // Handle either response format from the API
      let userData: User;
      
      if (response.user) {
        // Standard response format with user object
        userData = authApi.mapApiUserToUser(response.user);
      } else if (response.email && response.user_id) {
        // Alternative response format with direct fields
        userData = authApi.mapApiUserToUser(undefined, response.email, response.user_id);
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
      
      // Store user info and token based on rememberMe preference
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('isLoggedIn', 'true');
      storage.setItem('user', JSON.stringify(userData));
      saveAuthToken(response.token, rememberMe);
      
      setUser(userData);
      setIsLoggedIn(true);
      
      // Fetch company information after successful login
      try {
        const companyData = await authApi.getCompany();
        console.log('AuthContext: Post-login company data:', companyData);
        setCompany(companyData);
        
        // Check if we need to redirect to company setup
        if (companyData.owner === null) {
          console.log('AuthContext: Post-login redirect needed, current path:', window.location.pathname);
          // Only redirect if we're not already on the setup page
          if (window.location.pathname !== '/company-setup') {
            console.log('AuthContext: Redirecting to company setup after login');
            navigate('/company-setup');
          }
        } else {
          console.log('AuthContext: Company already set up, redirecting to dashboard');
          showSuccess('Successfully fetched company information');
          // Redirect to dashboard if company is already set up and we're on login page
          if (window.location.pathname === '/login') {
            navigate('/');
          }
        }
      } catch (error: any) {
        console.error('Error fetching company after login:', error);
        showError(error.message || 'Failed to fetch company information');
        setCompanyFetchFailed(true);
      }
      
      return;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to check company status and redirect to setup if needed
  const checkAndRedirectCompanySetup = async (): Promise<void> => {
    console.log('🏛️ checkAndRedirectCompanySetup called:', { isLoggedIn });
    if (!isLoggedIn) {
      return;
    }

    try {
      const companyData = await fetchCompany();
      
      // Check if we need to redirect to company setup
      if (companyData.owner === null) {
        const currentPath = window.location.pathname;
        console.log('AuthContext: Company setup needed, current path:', currentPath);
        
        // Only redirect if we're not already on the setup page
        if (currentPath !== '/company-setup') {
          console.log('AuthContext: Redirecting to company setup');
          navigate('/company-setup');
        }
      }
    } catch (error: any) {
      console.error('Error checking company setup status:', error);
      // Don't show error for this background check
    }
  };

  // Logout function
  const logout = () => {
    // Remove user data and token from storage
    clearAuthData();
    
    // Update state
    setUser(null);
    setCompany(null);
    setIsLoggedIn(false);
    setCompanyFetchFailed(false); // Reset the fetch failure flag
    
    // Navigate to login page
    navigate('/login');
  };

  // Function to force refresh company data (clears cache and fetches fresh data)
  const refreshCompanyData = async (): Promise<CompanyResponse> => {
    if (!isLoggedIn) {
      throw new Error('Not logged in');
    }
    
    // Clear existing company data to force a fresh fetch
    setCompany(null);
    setCompanyFetchFailed(false);
    
    try {
      const companyData = await authApi.getCompany();
      setCompany(companyData);
      return companyData;
    } catch (error: any) {
      console.error('Failed to refresh company data:', error);
      setCompanyFetchFailed(true);
      throw error;
    }
  };

  const value = {
    user,
    company,
    isLoggedIn,
    login,
    logout,
    isLoading,
    fetchCompany,
    refreshCompanyData,
    checkAndRedirectCompanySetup
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
